import React, { useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { AGENT_LINKS } from './links';
import { agentApi } from '@/lib/api';
import { Building, Users as UsersIcon, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/Loader';

function Tile({ label, value, sub, tone = 'default', icon: Icon }) {
  const tones = {
    default: 'bg-white border-slate-200',
    dark: 'bg-[#0D1B2A] text-white border-[#0D1B2A]',
    accent: 'bg-[#E6F1FF] border-[#BEDBFF]',
    warn: 'bg-amber-50 border-amber-200',
  };
  const isDark = tone === 'dark';
  return (
    <div className={`rounded-xl border p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div className={`text-[11px] font-semibold uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-slate-500'}`}>{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
      {sub && <div className={`mt-1 text-xs ${isDark ? 'text-white/60' : 'text-slate-500'}`}>{sub}</div>}
    </div>
  );
}

export default function AgentDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async (silent = false) => {
      const el = document.activeElement;
      if (silent && el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      if (silent && document.visibilityState === 'hidden') return;
      try { const { data } = await agentApi.dashboard(); if (mounted) setData(data); }
      catch { /* silent */ } finally { if (!silent) setLoading(false); }
    };
    load(false);
    const t = setInterval(() => load(true), 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <AppLayout links={AGENT_LINKS} testid={TID.agentSidebar} title="Mon portefeuille">
      {loading || !data ? (
        <Loader variant="full" />
      ) : (
        <>
          <p className="text-slate-500 -mt-4 mb-6 text-sm">
            Suivez la performance de vos bailleurs et vos échéances d'abonnement.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile tone="dark" icon={Building} label="Bailleurs" value={data.kpis.total_bailleurs} sub={`${data.kpis.active_bailleurs} actifs`} />
            <Tile icon={UsersIcon} label="Locataires suivis" value={data.kpis.total_locataires} />
            <Tile tone="warn" icon={Clock} label="Renouvellements ≤ 60j" value={data.kpis.expiring_soon} sub="Abonnements à relancer" />
            <Tile tone="accent" icon={DollarSign} label="Revenu du portefeuille" value={`$${data.kpis.revenue_usd.toLocaleString()}`} sub="Annuel — offres actives" />
          </div>

          <div className="mt-8 grid lg:grid-cols-3 gap-4">
            <div className="lb-card p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Bailleurs récents</div>
                  <div className="text-lg font-bold text-[#0D1B2A] mt-1">Dernières créations</div>
                </div>
              </div>
              {data.recent_bailleurs.length === 0 ? (
                <div className="py-10 text-center text-slate-500 text-sm">
                  Aucun bailleur pour l'instant. Cliquez sur « Mes bailleurs » pour en créer un.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.recent_bailleurs.map((b) => (
                    <div key={b.id} className="py-3 flex items-center justify-between cursor-pointer" onClick={() => nav(`/agent/bailleurs/${b.id}`)}>
                      <div>
                        <div className="text-sm font-semibold text-[#0D1B2A]">{b.prenom} {b.nom}</div>
                        <div className="text-xs text-slate-500">{b.email} · {b.ville || b.pays}</div>
                      </div>
                      <div className="text-xs text-slate-500">{new Date(b.created_at).toLocaleDateString('fr-FR')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lb-card p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Statuts d'abonnement</div>
              <div className="mt-4 space-y-2">
                {Object.entries(data.subs_by_status).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-slate-700">{k}</span>
                    <span className="font-bold text-[#0D1B2A]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
